import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, type AttributionUser, type HistoryItem, type ItemKind, type Project, type Thread } from "../api/client";
import AttributionBadge from "../components/Attribution";
import { setCurrentProject } from "../hooks/useCurrentProject";

interface InboxRow {
  id: number;
  kind: ItemKind;
  title: string;
  preview: string;
  when: number;
  meta: string;
  created_by: AttributionUser | null;
}

function rowTime(ms: number): string {
  const d = new Date(ms);
  const today = new Date();
  if (
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate()
  ) {
    return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  }
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function fromHistory(it: HistoryItem): InboxRow {
  if (it.kind === "chat") {
    return {
      id: it.id,
      kind: "chat",
      title: it.title,
      preview: it.preview,
      when: it.updated_at,
      meta: `${it.message_count} msgs`,
      created_by: it.created_by,
    };
  }
  if (it.kind === "task") {
    return {
      id: it.id,
      kind: "task",
      title: it.title,
      preview: it.preview,
      when: it.created_at,
      meta: "task result",
      created_by: it.created_by,
    };
  }
  return {
    id: it.id,
    kind: "research",
    title: it.title,
    preview: it.preview,
    when: it.created_at,
    meta: `${it.approved_count}/${it.candidate_count} ingested`,
    created_by: it.created_by,
  };
}

function fromThread(t: Thread): InboxRow {
  return {
    id: t.id,
    kind: "chat",
    title: t.title,
    preview: "Unscoped conversation. Move it into a study when it becomes durable research context.",
    when: t.updated_at,
    meta: "chat",
    created_by: t.created_by,
  };
}

export default function InboxPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [rows, setRows] = useState<InboxRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const activeProjects = useMemo(() => projects.filter((p) => !p.archived), [projects]);

  function refresh() {
    setLoading(true);
    setError(null);
    setCurrentProject({ kind: "inbox" });
    Promise.all([api.listProjects(true), api.listHistory({ inbox: true }), api.listThreads({ inbox: true })])
      .then(([ps, history, threads]) => {
        const historyRows = history.map(fromHistory);
        const historyChatIds = new Set(historyRows.filter((r) => r.kind === "chat").map((r) => r.id));
        const threadRows = threads.filter((t) => !historyChatIds.has(t.id)).map(fromThread);
        setProjects(ps);
        setRows([...historyRows, ...threadRows].sort((a, b) => b.when - a.when));
      })
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    refresh();
  }, []);

  async function moveRow(row: InboxRow, projectId: string) {
    const nextProjectId = projectId ? Number(projectId) : null;
    await api.setItemProject(row.kind, row.id, nextProjectId);
    setRows((current) => current.filter((r) => !(r.kind === row.kind && r.id === row.id)));
  }

  async function moveAll(projectId: string) {
    if (!projectId || rows.length === 0) return;
    const nextProjectId = Number(projectId);
    await Promise.all(rows.map((row) => api.setItemProject(row.kind, row.id, nextProjectId)));
    setRows([]);
  }

  async function removeRow(row: InboxRow) {
    if (!window.confirm(`Delete "${row.title}"?`)) return;
    if (row.kind === "chat") await api.deleteThread(row.id);
    else if (row.kind === "task") await api.deleteTaskRun(row.id);
    else await api.deleteResearchRun(row.id);
    setRows((current) => current.filter((r) => !(r.kind === row.kind && r.id === row.id)));
  }

  return (
    <>
      <div className="main-header">
        <div>
          <span className="crumb">/ inbox</span>
          <h2>Inbox</h2>
        </div>
        <span className="spacer" />
        <select
          value=""
          onChange={(e) => moveAll(e.target.value)}
          className="ar-inbox-bulk-select"
          disabled={rows.length === 0 || activeProjects.length === 0}
          aria-label="Move all to a study"
        >
          <option value="">Move all to a study...</option>
          {activeProjects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </div>
      <div className="main-body">
        <p className="ar-inbox-head">
          Conversations and runs started without a study first. Move them into a study when they become durable research context.
        </p>
        {loading && <p className="muted">Loading...</p>}
        {error && <p className="error">{error}</p>}
        <div className="ar-inbox">
          {rows.map((row) => (
            <div key={`${row.kind}-${row.id}`} className="ar-inbox-row">
              <span className="ar-inbox-when">{rowTime(row.when)}</span>
              <button
                type="button"
                className="ar-inbox-open"
                onClick={() => {
                  if (row.kind === "chat") navigate(`/chat/${row.id}`);
                  else if (row.kind === "task") navigate("/history");
                  else navigate("/research");
                }}
              >
                <span className="ar-inbox-title">{row.title}</span>
                <AttributionBadge createdBy={row.created_by} compact />
                <span className="ar-inbox-preview">{row.preview}</span>
              </button>
              <label className="ar-inbox-assign">
                <span>move to</span>
                <select value="" onChange={(e) => moveRow(row, e.target.value)}>
                  <option value="">inbox</option>
                  {activeProjects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </label>
              <span className="ar-inbox-actions">
                <span className={`ar-inbox-meta ${row.kind}`}>{row.meta}</span>
                <button type="button" className="ar-inbox-delete" onClick={() => removeRow(row)} aria-label={`Delete ${row.title}`}>
                  x
                </button>
              </span>
            </div>
          ))}
          {!loading && rows.length === 0 && (
            <div className="empty-study-state">
              <h3>Inbox is clear</h3>
              <p className="muted">Unscoped chats and runs will appear here until they are moved into a study.</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
